import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { ConfigurationService } from './configuration.service';
import { UserClassesTaken } from '@models/user-classes-taken';
import { environment } from '@environments/environment';

export interface ActivityType {
  VIDEO: 1;
  HANDOUT: 2;
  QUIZ: 3;
}

export interface RestrictionResult {
  canAccess: boolean;
  errorMessage?: string;
  errorMessage2?: string;
  redirectLink?: string;
  nextClass?: number;
  nextActivity?: number;
  nextActivityName?: string;
}

export interface NextActivity {
  classId: number;
  activityType: number;
  activityName: string;
  redirectLink?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CourseRestrictionService {
  
  private readonly ACTIVITY_TYPES = {
    VIDEO: 1,
    HANDOUT: 2,
    QUIZ: 3
  } as const;

  private readonly ACTIVITY_NAMES = {
    1: 'Video',
    2: 'E-book', 
    3: 'Quiz'
  } as const;

  constructor(
    private authService: AuthService,
    private configurationService: ConfigurationService
  ) {}

  /**
   * Calculate total cooldown period in minutes from configuration service
   */
  private calculateCooldownMinutes(): number {
    return this.configurationService.calculateCooldownMinutes();
  }

  /**
   * Main method to check if user can access a specific class activity
   */
  checkAccess(
    targetClassId: number, 
    targetActivity: number, 
    userClassesTakenList: UserClassesTaken[],
    hasOverride: boolean = false
  ): RestrictionResult {
    
    // Admin bypass
    if (this.authService.isAdmin) {
      return { canAccess: true };
    }

    // Course completed bypass
    if (this.isCourseCompleted()) {
      return { canAccess: true };
    }

    // Class override bypass
    if (this.hasClassOverride(targetClassId, userClassesTakenList)) {
      return { canAccess: true };
    }

    // Find next required activity
    const nextActivity = this.findNextRequiredActivity(userClassesTakenList);

    // Check waiting period for new classes
    const waitingPeriodCheck = this.checkWaitingPeriod(
      targetClassId,
      targetActivity,
      nextActivity,
      userClassesTakenList
    );

    if (!waitingPeriodCheck.canAccess) {
      return waitingPeriodCheck;
    }

    // **NEW: Handout override for current accessible class**
    if (targetActivity === this.ACTIVITY_TYPES.HANDOUT && hasOverride && nextActivity) {
      // Allow handout access if we're in the current class or a completed class
      if (targetClassId <= nextActivity.classId) {
        //console.log("Handout override active for accessible class:", targetClassId);
        return { canAccess: true };
      }
    }

    
    // Check sequential access
    const sequentialCheck = this.checkSequentialAccess(
      targetClassId, 
      targetActivity, 
      nextActivity,
      hasOverride
    );
    
    return sequentialCheck;
  }

  /**
   * Check if course is completed
   */
  private isCourseCompleted(): boolean {
    try {
      return JSON.parse(this.authService.getCourseCompleted());
    } catch {
      return false;
    }
  }

  /**
   * Check if class has manual override
   */
  private hasClassOverride(classId: number, userClassesTakenList: UserClassesTaken[]): boolean {
    const classIndex = classId - 1;
    return userClassesTakenList[classIndex]?.isAvailable || false;
  }

  /**
   * Find the next activity that needs to be completed
   */
  private findNextRequiredActivity(userClassesTakenList: UserClassesTaken[]): NextActivity | null {
    for (const userClass of userClassesTakenList) {
      if (!userClass.isVideoCompleted) {
        return {
          classId: userClass.classId,
          activityType: this.ACTIVITY_TYPES.VIDEO,
          activityName: this.ACTIVITY_NAMES[1],
          redirectLink: `/video/${userClass.classId}`
        };
      }
      
      if (!userClass.isHandoutCompleted) {
        return {
          classId: userClass.classId,
          activityType: this.ACTIVITY_TYPES.HANDOUT,
          activityName: this.ACTIVITY_NAMES[2]
        };
      }
      
      if (!userClass.isQuizCompleted) {
        return {
          classId: userClass.classId,
          activityType: this.ACTIVITY_TYPES.QUIZ,
          activityName: this.ACTIVITY_NAMES[3],
          redirectLink: `/quiz/${userClass.classId}`
        };
      }
    }
    
    return null; // All activities completed
  }

  /**
   * Check if user is trying to access activities in correct sequence
   */
  private checkSequentialAccess(
    targetClassId: number,
    targetActivity: number,
    nextActivity: NextActivity | null,
    hasOverride: boolean = false
  ): RestrictionResult {
    
    if (!nextActivity) {
      return { canAccess: true }; // All activities completed
    }

    // Trying to access a class beyond the next required
    if (targetClassId > nextActivity.classId) {
      return {
        canAccess: false,
        errorMessage: `The next activity to finish is Class ${nextActivity.classId} Activity ${nextActivity.activityName}`,
        redirectLink: nextActivity.redirectLink,
        nextClass: nextActivity.classId,
        nextActivity: nextActivity.activityType,
        nextActivityName: nextActivity.activityName
      };
    }

    // Trying to access an activity beyond the next required in the same class
    if (targetClassId === nextActivity.classId && targetActivity > nextActivity.activityType) {
      return {
        canAccess: false,
        errorMessage: `The next activity to finish is Class ${nextActivity.classId} Activity ${nextActivity.activityName}`,
        redirectLink: nextActivity.redirectLink,
        nextClass: nextActivity.classId,
        nextActivity: nextActivity.activityType,
        nextActivityName: nextActivity.activityName
      };
    }

    return { canAccess: true };
  }



  /**
   * Check waiting period between classes
   */
  private checkWaitingPeriod(
    targetClassId: number,
    targetActivity: number,
    nextActivity: NextActivity | null,
    userClassesTakenList: UserClassesTaken[]
  ): RestrictionResult {
    
    // Skip waiting period check for first class
    if (targetClassId === 1) {
      return { canAccess: true };
    }

    // Only check waiting period when starting a new class (video or handout activity)
    if (!(targetClassId === nextActivity?.classId && 
          (targetActivity === this.ACTIVITY_TYPES.VIDEO || targetActivity === this.ACTIVITY_TYPES.HANDOUT) && 
          (nextActivity?.activityType === this.ACTIVITY_TYPES.VIDEO || nextActivity?.activityType === this.ACTIVITY_TYPES.HANDOUT))) {
      return { canAccess: true };
    }

    const previousClassIndex = targetClassId - 2;
    const previousClass = userClassesTakenList[previousClassIndex];
    
    if (!previousClass?.whenClassCompleted) {
      return { canAccess: true }; // Previous class not completed, let sequential check handle it
    }

    const completionDate = new Date(previousClass.whenClassCompleted);
    const requiredWaitDate = new Date(completionDate);
    // console.log(`CompletionDate ${completionDate}` )
    // console.log(`Required Wait Date: ${requiredWaitDate}`)
    
    // Add waiting period from environment using calculated total minutes
    const waitingPeriodMinutes = this.calculateCooldownMinutes();
    const waitingPeriodMs = waitingPeriodMinutes * 60 * 1000;
    requiredWaitDate.setTime(requiredWaitDate.getTime() + waitingPeriodMs);
    
    const now = new Date();
    // console.log(`Now: ${now}`);
    // console.log(`Required Wait Date after adding ${waitingPeriodMinutes} minutes: ${requiredWaitDate}`);
    
    if (now < requiredWaitDate) {
      
      const previousClassNumber = targetClassId - 1;
      const waitingPeriodDisplay = this.getWaitingPeriodDisplayText();
      return {
        canAccess: false,
        errorMessage: `You must wait ${waitingPeriodDisplay} after completing class ${previousClassNumber} before accessing the next class`,
        errorMessage2: `You will be able to access the next activity at: ${requiredWaitDate.toLocaleString()}`
      };
    }


    return { canAccess: true };
  }

  /**
   * Get user-friendly display text for waiting period
   */
  private getWaitingPeriodDisplayText(): string {
    const settings = this.configurationService.getCurrentCourseSettings();
    const cooldownConfig = settings.classCooldownPeriod;
    
    // Build display text from the configuration components
    const parts: string[] = [];
    
    if (cooldownConfig.days && cooldownConfig.days > 0) {
      parts.push(`${cooldownConfig.days} day${cooldownConfig.days !== 1 ? 's' : ''}`);
    }
    
    if (cooldownConfig.hours && cooldownConfig.hours > 0) {
      parts.push(`${cooldownConfig.hours} hour${cooldownConfig.hours !== 1 ? 's' : ''}`);
    }
    
    if (cooldownConfig.minutes && cooldownConfig.minutes > 0) {
      parts.push(`${cooldownConfig.minutes} minute${cooldownConfig.minutes !== 1 ? 's' : ''}`);
    }
    
    // Join parts with 'and' for the last item
    if (parts.length === 0) {
      return 'no waiting period';
    } else if (parts.length === 1) {
      return parts[0];
    } else if (parts.length === 2) {
      return parts.join(' and ');
    } else {
      const lastPart = parts.pop();
      return parts.join(', ') + ', and ' + lastPart;
    }
  }

  /**
   * Convenience methods for checking specific activity types
   */
  canAccessVideo(classId: number, userClassesTakenList: UserClassesTaken[]): RestrictionResult {
    return this.checkAccess(classId, this.ACTIVITY_TYPES.VIDEO, userClassesTakenList);
  }

  canAccessHandout(classId: number, userClassesTakenList: UserClassesTaken[], isHandoutAvailable: boolean = false): RestrictionResult {
    let result = this.checkAccess(classId, this.ACTIVITY_TYPES.HANDOUT, userClassesTakenList, isHandoutAvailable);
    return result;
    return this.checkAccess(classId, this.ACTIVITY_TYPES.HANDOUT, userClassesTakenList, isHandoutAvailable);
  }

  canAccessQuiz(classId: number, userClassesTakenList: UserClassesTaken[]): RestrictionResult {
    return this.checkAccess(classId, this.ACTIVITY_TYPES.QUIZ, userClassesTakenList);
  }
}
